import { useDailyRoom } from '@web-app/components/CallV2/hooks/useDailyRoom';
import { NoteTemplate } from '@web-app/components/ChartingNoteEditor/types';
import { trpc } from '@web-app/utils/trpc';
import { useCallback, useMemo } from 'react';

export interface InSessionTemplateSource {
  /** Lightweight list for the picker. Entries carry no `content`. */
  templates: NoteTemplate[];
  /**
   * Resolves a template's full content by title. The content is fetched (and
   * cached by React Query, so "Switch Template" reuses it) by slug.
   */
  resolveTemplate: (title: string) => Promise<NoteTemplate | undefined>;
  isLoading: boolean;
}

/**
 * Single entry point for the in-session template picker. Templates come from the
 * `in_session_templates` DB table via tRPC. The base templates are seeded as
 * rows, so the generated TipTap templates are NOT merged in.
 */
export const useInSessionTemplateSource = (): InSessionTemplateSource => {
  const { roomId } = useDailyRoom();
  const utils = trpc.useUtils();

  const listQuery = trpc.inSessionTemplates.listForSession.useQuery(
    { roomId: roomId ?? '' },
    { enabled: !!roomId },
  );

  const dbTemplates = useMemo<NoteTemplate[]>(
    () =>
      (listQuery.data ?? []).map((t) => ({
        title: t.title,
        editor: t.editor,
        content: '',
      })),
    [listQuery.data],
  );

  const titleToSlug = useMemo(() => {
    const map = new Map<string, string>();
    (listQuery.data ?? []).forEach((t) => map.set(t.title, t.slug));
    return map;
  }, [listQuery.data]);

  const resolveTemplate = useCallback(
    async (title: string): Promise<NoteTemplate | undefined> => {
      const slug = titleToSlug.get(title);
      if (!slug) {
        return undefined;
      }

      const data = await utils.inSessionTemplates.getBySlugForSession.fetch({
        slug,
      });

      // Prefer TipTap JSON only for TipTap-authored rows; quill rows (the
      // common case) use their HTML string, which the editor parses.
      const content =
        data.editor === 'tiptap' && data.contentJson
          ? data.contentJson
          : data.content;

      return { title: data.title, editor: data.editor, content };
    },
    [titleToSlug, utils],
  );

  return {
    templates: dbTemplates,
    resolveTemplate,
    isLoading: listQuery.isLoading,
  };
};
